import React, { useState, useEffect, useCallback, useRef } from 'react'; // Added useRef
import { useLocation, Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { apiClient } from '../../apiClient';
import { auth } from '../../firebase';

const formatDate = (timestamp) => {
  if (!timestamp) return 'No date provided';
  const seconds = timestamp.seconds || timestamp._seconds;
  if (typeof seconds !== 'number') return 'Invalid Date';
  return new Date(seconds * 1000).toLocaleString('ja-JP');
};

const CsvHistory = () => {
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [errors, setErrors] = useState({});
  const [selectedJob, setSelectedJob] = useState(null);
  const [lastDocId, setLastDocId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Use a ref to track if initial fetch has been done to prevent loops
  const isInitialFetched = useRef(false);

  const [searchInputs, setSearchInputs] = useState({
    billwaveCustomerId: '',
    userId: '',
    startDate: '',
    endDate: '',
    status: ''
  });
  const [activeFilters, setActiveFilters] = useState({});

  const fetchJobs = useCallback(async (filters, isLoadMore = false, currentLastDocId = null) => {
    // Determine which loading state to use
    if (isLoadMore ? loadingMore : loading) return;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();

      const params = { ...filters };
      // Use the passed currentLastDocId or the state one
      const effectiveLastDocId = isLoadMore ? (currentLastDocId || lastDocId) : null;
      if (effectiveLastDocId) {
        params.lastDocId = effectiveLastDocId;
      }

      const queryString = new URLSearchParams(params).toString();
      const url = `/v1/admin/csv-jobs?${queryString}`;
      
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const response = await apiClient.get(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch CSV jobs');
      }
      
      setJobs(prev => isLoadMore ? [...prev, ...data] : data);
      
      if (data.length < 50) {
        setHasMore(false);
      } else {
        setLastDocId(data[data.length - 1].id);
        setHasMore(true);
      }
    } catch (error) {
      console.error('CSVジョブの取得中にエラーが発生しました:', error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
      setLoading(false);
    }
  }, [lastDocId, loading, loadingMore]);

  // Handle initial search with query parameters from URL
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        const queryParams = new URLSearchParams(location.search);
        const customerIdFromUrl = queryParams.get('billwaveCustomerId') || '';
        const userIdFromUrl = queryParams.get('userId') || '';
        
        const initialFilters = {
          billwaveCustomerId: customerIdFromUrl,
          userId: userIdFromUrl,
          startDate: '',
          endDate: '',
          status: ''
        };

        setSearchInputs(initialFilters);
        
        const cleanFilters = Object.fromEntries(
          Object.entries(initialFilters).filter(([_, value]) => value !== '')
        );
        
        setActiveFilters(cleanFilters);
        setJobs([]);
        setLastDocId(null);
        setHasMore(true);
        // Direct call without depending on fetchJobs in useEffect deps
        fetchJobs(cleanFilters, false, null);
      }
    });
    return () => unsubscribe();
    // Only re-run when location.search changes, NOT on fetchJobs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchErrors = async (jobId) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      const config = { headers: { 'Authorization': `Bearer ${token}` } };

      const response = await apiClient.get(`/v1/admin/csv-jobs/${jobId}/errors`, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to fetch errors for job ${jobId}`);
      }
      setErrors(prev => ({ ...prev, [jobId]: data }));
    } catch (error) {
      console.error(`ジョブ ${jobId} のエラー取得中にエラーが発生しました:`, error);
      setErrors(prev => ({ ...prev, [jobId]: [] }));
    }
  };

  const handleSearchChange = (e) => {
    setSearchInputs({ ...searchInputs, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
      const cleanFilters = Object.fromEntries(
        Object.entries(searchInputs).filter(([_, value]) => value !== '' && value !== null)
      );
      setActiveFilters(cleanFilters);
      setLastDocId(null);
      setJobs([]);
      fetchJobs(cleanFilters, false, null);
  };

  const handleReset = () => {
      const emptyInputs = { billwaveCustomerId: '', userId: '', startDate: '', endDate: '', status: '' };
      setSearchInputs(emptyInputs);
      setActiveFilters({});
      setLastDocId(null);
      setJobs([]);
      fetchJobs({}, false, null);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
        fetchJobs(activeFilters, true);
    }
  }

  const toggleJobDetails = (job) => {
    if (selectedJob && selectedJob.id === job.id) {
      setSelectedJob(null);
    } else {
      setSelectedJob(job);
      if (job.status === 'failed' && !errors[job.id]) {
        fetchErrors(job.id);
      }
    }
  };
  
  const JobRow = ({ job }) => (
    <>
      <tr onClick={() => toggleJobDetails(job)} className="cursor-pointer hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatDate(job.created_at)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.user_id}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${job.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{job.status}</span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <button className="text-indigo-600 hover:text-indigo-900">{selectedJob && selectedJob.id === job.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}</button>
        </td>
      </tr>
      {selectedJob && selectedJob.id === job.id && (
        <tr><td colSpan="4" className="px-6 py-4 bg-gray-50"><JobDetails job={selectedJob} jobErrors={errors[selectedJob.id]} /></td></tr>
      )}
    </>
  );

  const JobDetails = ({ job, jobErrors }) => (
    <div className="bg-white p-4 rounded-lg shadow-inner">
       <div className="grid grid-cols-2 gap-4 text-sm mb-4">
        <p><strong>実行日時:</strong> {formatDate(job.created_at)}</p>
        <p><strong>CSVファイル名:</strong> {job.original_file_name}</p>
        <p><strong>CSV種類:</strong> {job.type}</p>
        <p><strong>BillWaveお客様ID:</strong> {job.tenant_id}</p>
        <p><strong>実行ユーザー:</strong> {job.user_id}</p>
        <p><strong>総件数:</strong> {job.total_rows}</p>
        <p><strong>成功件数:</strong> {job.success_count}</p>
        <p><strong>失敗件数:</strong> {job.error_count}</p>
        <p><strong>ステータス:</strong> <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${job.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{job.status}</span></p>
      </div>

      <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200">
        <Link to={`/admin/companies/${job.tenant_id}`} className="text-indigo-600 hover:text-indigo-800 flex items-center text-xs">
          <ExternalLink size={14} className="mr-1" />
          企業情報を開く
        </Link>
        <Link to={`/admin/users/${job.user_id}`} className="text-indigo-600 hover:text-indigo-800 flex items-center text-xs">
          <ExternalLink size={14} className="mr-1" />
          ユーザー情報を開く
        </Link>
      </div>

      {job.status === 'failed' && (
        <div className="mt-6">
          <h4 className="text-md font-semibold text-gray-800 mb-2">エラー一覧</h4>
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">行番号</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">エラーコード</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">エラーメッセージ</th></tr></thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobErrors && jobErrors.length > 0 ? jobErrors.map((error, index) => (
                  <tr key={error.id || index}><td className="px-6 py-4 text-sm text-gray-500">{error.error_row}</td><td className="px-6 py-4 text-sm text-gray-500">{error.error_code}</td><td className="px-6 py-4 text-sm text-gray-500">{error.error_message}</td></tr>
                )) : (<tr><td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">{jobErrors ? 'エラーはありません' : 'エラー情報を読み込んでいます...'}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">CSV処理履歴</h2>
      <div className="p-4 bg-gray-50 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <input type="text" name="billwaveCustomerId" placeholder="BillWaveお客様ID" value={searchInputs.billwaveCustomerId} onChange={handleSearchChange} className="p-2 border rounded"/>
          <input type="text" name="userId" placeholder="実行ユーザーID" value={searchInputs.userId} onChange={handleSearchChange} className="p-2 border rounded"/>
          <input type="datetime-local" name="startDate" value={searchInputs.startDate} onChange={handleSearchChange} className="p-2 border rounded"/>
          <input type="datetime-local" name="endDate" value={searchInputs.endDate} onChange={handleSearchChange} className="p-2 border rounded"/>
          <select name="status" value={searchInputs.status} onChange={handleSearchChange} className="p-2 border rounded">
            <option value="">すべてのステータス</option>
            <option value="completed">完了</option>
            <option value="failed">失敗</option>
          </select>
        </div>
        <div className="mt-4 flex justify-end gap-2">
            <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 shadow-sm">検索</button>
            <button onClick={handleReset} className="px-4 py-2 bg-gray-300 text-gray-800 font-semibold rounded-md hover:bg-gray-400 shadow-sm">リセット</button>
        </div>
      </div>
      {(loading && jobs.length === 0) ? <p className="text-center py-4">読み込み中...</p> : (
        <>
          <div className="overflow-x-auto shadow-md sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100"><tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">実行日時</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">実行ユーザー</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th><th className="relative px-6 py-3"><span className="sr-only">詳細</span></th></tr></thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map(job => <JobRow key={job.id} job={job} />)}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="mt-6 text-center">
              <button onClick={handleLoadMore} disabled={loadingMore} className="px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400">
                {loadingMore ? '読み込み中...' : '次の50件を読み込む'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CsvHistory;
